import { useCallback, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { CreateSingleQuoteCommand } from "api/quote"
import { useAppDispatch, useAppSelector } from "app/hooks"
import { RootState } from "app/store"
import { downloadDocumentByStoredId } from "services/documentsAPI"
import { selectIsInShoppingCart } from "features/subscription-process/pricing-process/slices/pricing/selectors"
import { addShoppingCartItem, removeShoppingCartItem } from "features/subscription-process/pricing-process/slices/pricing/pricingSlice"
import { mapModifiedFees } from "features/subscription-process/pricing-process/slices/pricing/utils/customFees"
import { ContractFunnel } from "features/subscription-process/pricing-process/types/interfaces"
import { useSubscriptionProcessState, useRiskData } from "features/subscription-process/contexts/selectors/selectors"
import { useDocumentViewerDialog } from "components/domain/Document/useDocumentViewerDialog"
import { pricingAPI } from "services/swagger-api"
import { FileTypeEnum } from "api/actor"
import { parseApiError } from "shared/utils/apiValidationErrors"
import { documentsForScope, useProductContractualDocuments } from "./useProductContractualDocuments"

// Statut "En cours" = contrat collectif signé/actif (cf. backend Codes.StatutsContrats.EnCours).
const SIGNED_CONTRACT_STATUT_CODE = "EC"

export const useFunnelCardFooter = (contractFunnel: ContractFunnel) => {
  const isInShoppingCart = useAppSelector(selectIsInShoppingCart(contractFunnel.hash || ''))
  const dispatch = useAppDispatch()
  const [isDownloading, setIsDownloading] = useState<string | null>(null)
  const [isGeneratingDevis, setIsGeneratingDevis] = useState(false)

  const { branchCode } = useSubscriptionProcessState()
  const riskData = useRiskData() as {
    person?: {
      id?: string
      email?: string
      type?: 'morale' | 'physique'
      firstName?: string
      lastName?: string
      companyName?: string
    }
  } | null
  const riskId = useAppSelector((state: RootState) => state.pricing.riskId)
  const tunnelFeeValues = useAppSelector((state: RootState) => state.pricing.tunnelFeeValues)
  const { openDocumentViewer } = useDocumentViewerDialog()

  // Cartouche d'un contrat collectif : elle porte l'identifiant du contrat collectif.
  // Tant qu'il n'est pas signé (statut != "EC"), on interdit l'ajout au panier ;
  // seul un devis à la volée sera possible (cf. Partie B).
  const isCollectiveContract = !!contractFunnel.contractIdentifier
  const isCollectiveContractDraft =
    isCollectiveContract &&
    !!contractFunnel.contractStatutCode &&
    contractFunnel.contractStatutCode !== SIGNED_CONTRACT_STATUT_CODE

  // Règle d'or : seul isSuccess === true est ajoutable. false = produit inéligible
  // (contrainte produit) OU erreur de tarif → non ajoutable, grisé, prix masqué.
  const isIneligible = contractFunnel.isSuccess === false
  const errorMessage = contractFunnel.errorMessage ?? null

  // Documents contractuels réellement déposés sur le produit : la carte n'affiche que ceux-là.
  const productDocuments = useProductContractualDocuments(contractFunnel.product?.id)
  const isGroupCard = !!contractFunnel.contractIdentifier
  const contractualDocuments = useMemo(
    () => documentsForScope(productDocuments, isGroupCard),
    [productDocuments, isGroupCard]
  )

  const onDownload = useCallback(async (storedFileId: string) => {
    setIsDownloading(storedFileId)
    try {
      const res = await downloadDocumentByStoredId(storedFileId);
      const blob = new Blob([res], { type: `application/pdf` });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error(error)
      toast.error("Le document n'a pas pu être téléchargé.")
    } finally {
      setIsDownloading(null)
    }
  }, [])

  // Fonction pour basculer l'état du panier
  const toggleCart = useCallback(() => {
    // Contrat collectif non signé : pas d'ajout au panier (devis à la volée uniquement).
    if (isCollectiveContractDraft) return
    // Produit inéligible / non tarifé : jamais ajoutable.
    if (isIneligible) return
    if (isInShoppingCart) {
      dispatch(removeShoppingCartItem(contractFunnel.hash || ''))
    } else {
      dispatch(addShoppingCartItem(contractFunnel))
    }
  }, [isInShoppingCart, contractFunnel, dispatch, isCollectiveContractDraft, isIneligible]);

  // Devis à la volée (toutes cartouches : individuel, collectif signé ou brouillon) : génère le PDF
  // de devis sans rien persister, puis l'ouvre dans la visionneuse (téléchargement + envoi mail).
  // Cf. backend POST /comparateurs/devis-simulation-pdf.
  const generateDevisOnTheFly = useCallback(async () => {
    if (isGeneratingDevis) return
    setIsGeneratingDevis(true)
    try {
      const createSingleQuoteCommand: CreateSingleQuoteCommand = {
        funnelId: contractFunnel.id,
        isIndividual: !contractFunnel.contractIdentifier,
        contractIdentifier: contractFunnel.contractIdentifier,
        supportCode: contractFunnel.supportCode,
        fees: mapModifiedFees(contractFunnel.fees, tunnelFeeValues),
        variables: contractFunnel.variables.map((variable) => ({ idVariableValue: variable.variableValueId, value: variable.value })),
        guarantees: contractFunnel.guarantees.map((guarantee) => ({
          guaranteeId: guarantee.guaranteeTunnelId,
          deductible: guarantee.deductible ? { ...guarantee.deductible, value: guarantee.deductible?.value } : undefined,
          ceiling: guarantee.ceiling ? { ...guarantee.ceiling, value: guarantee.ceiling?.value } : undefined,
        })),
        branchCode,
        personId: riskData?.person?.id,
        riskId,
      } as CreateSingleQuoteCommand

      const response = await pricingAPI.generateDevisSimulationPdf({ createSingleQuoteCommand })

      if (response?.storedFileId) {
        // Destinataire pré-rempli : le souscripteur du dossier (même pattern que
        // le comparateur emprunteur) — nom complet ou raison sociale.
        const person = riskData?.person
        const recipientName =
          person?.type === 'morale'
            ? person.companyName
            : [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim()

        openDocumentViewer({
          title: `Devis ${contractFunnel.product?.label ?? ''}`.trim(),
          storedFileId: response.storedFileId,
          mimeType: 'application/pdf',
          showDefaultActions: true,
          emailConfig: {
            storedFileId: response.storedFileId,
            risqueAssureId: riskId ?? '',
            sendTo: person?.email
              ? [{ email: person.email, name: recipientName || undefined }]
              : undefined,
            fileTypeInt: FileTypeEnum.DevisPdfGenerer,
            fileName: `Devis_${contractFunnel.product?.label ?? 'simulation'}.pdf`,
          },
        })
      }
    } catch (error) {
      console.error(error)
      // Message du back (ex. 403 « aucune personne morale ») plutôt qu'un message générique.
      const { message } = await parseApiError(error)
      toast.error(message || "Une erreur est survenue lors de la génération du devis.")
    } finally {
      setIsGeneratingDevis(false)
    }
  }, [isGeneratingDevis, contractFunnel, branchCode, riskData, riskId, tunnelFeeValues, openDocumentViewer])

  return {
    isInShoppingCart,
    toggleCart,
    isDownloading,
    onDownload,
    contractualDocuments,
    isCollectiveContractDraft,
    isIneligible,
    errorMessage,
    generateDevisOnTheFly,
    isGeneratingDevis
  }
}

